import { Controller } from "@hotwired/stimulus"
import { ping } from "../lib/ping"
import { trackEvent } from "../lib/analytics"

const COUNT_AFTER = 1500

// Instant, in-page search over a guide's events (title, venue, area, type and description).
export default class extends Controller {
  static targets = ["input", "card", "section", "empty", "hideWhileSearching", "indicator"]
  static values = { guide: String }
  static outlets = ["guide-rest"]

  disconnect() {
    clearTimeout(this.countTimer)
  }

  filter() {
    const terms = normalize(this.inputTarget.value).split(/\s+/).filter(Boolean)
    const searching = terms.length > 0

    // A search goes through every row, so the rows a long page left out are fetched, then searched too
    if (searching && this.hasGuideRestOutlet && this.guideRestOutlet.pending) {
      this.guideRestOutlet.load().then(() => this.filter(), () => {})
    }

    this.cardTargets.forEach(card => {
      const text = card.dataset.searchNormalized ??= normalize(`${card.textContent} ${card.dataset.search || ""}`)
      card.classList.toggle("search-hidden", searching && !terms.every(term => text.includes(term)))
    })

    let matches = 0
    this.sectionTargets.forEach(section => {
      const visible = section.querySelectorAll("[data-guide-search-target='card']:not(.search-hidden)").length
      section.classList.toggle("search-hidden", visible === 0)
      matches += visible
    })

    this.hideWhileSearchingTargets.forEach(element => { element.hidden = searching })
    this.indicatorTargets.forEach(element => element.toggleAttribute("data-active", searching))
    if (this.hasEmptyTarget) this.emptyTarget.hidden = !(searching && matches === 0)

    this.dispatch("changed", { detail: { searching, matches } })
    this.countSearch(searching ? terms.join(" ") : "", matches)
  }

  // Counted once typing stops, so a search counts once rather than once per letter
  countSearch(query, matches) {
    clearTimeout(this.countTimer)
    if (query.length < 3 || query === this.lastCounted) return

    this.countTimer = setTimeout(() => {
      this.lastCounted = query
      ping("search", { q: query, matches, guide: this.guideValue })
      // The words searched stay out of it: they're whatever the visitor typed
      trackEvent("search", { guide: this.guideValue, matches, found: matches > 0 }, this.element)
    }, COUNT_AFTER)
  }

  clear() {
    if (!this.hasInputTarget) return

    this.inputTarget.value = ""
    this.filter()
    this.inputTarget.focus()
  }
}

function normalize(text) {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
}
